import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { CheckCircle2, Crown, ArrowRight, XCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [error, setError] = useState(null);
  const hasPolled = useRef(false);

  useEffect(() => {
    if (hasPolled.current) return;
    hasPolled.current = true;

    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('failed');
      setError('No session ID found');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      setError('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        toast.success('Welcome to Premium!');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        setError('Payment session expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('failed');
      setError('Error verifying payment');
    }
  };

  return (
    <div className="mobile-container asphalt-texture min-h-screen flex items-center justify-center p-4">
      <div className="glass rounded-xl p-8 text-center max-w-sm w-full">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-4 animate-spin" />
            <h1 className="font-oswald text-2xl font-bold uppercase mb-2">Verifying Payment</h1>
            <p className="text-gray-400 text-sm">Please wait while we confirm your subscription...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="relative w-20 h-20 mx-auto mb-4">
              <div className="absolute inset-0 bg-yellow-500/20 rounded-full animate-ping" />
              <div className="relative w-full h-full bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center">
                <Crown className="w-10 h-10 text-black" />
              </div>
            </div>
            <h1 className="font-oswald text-2xl font-bold uppercase mb-2">You're Premium!</h1>
            <p className="text-gray-400 text-sm mb-6">Welcome to the club. Enjoy all premium features!</p>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-left bg-white/5 rounded-lg p-3">
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-sm">Private routes unlocked</span>
              </div>
              <div className="flex items-center gap-3 text-left bg-white/5 rounded-lg p-3">
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-sm">Priority support enabled</span>
              </div>
            </div>

            <Button
              data-testid="continue-btn"
              className="w-full h-12 bg-bronze hover:bg-bronze-600 font-oswald uppercase tracking-wider mt-6"
              onClick={() => navigate('/dashboard')}
            >
              Start Planning
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="font-oswald text-2xl font-bold uppercase mb-2">Payment Issue</h1>
            <p className="text-gray-400 text-sm mb-6">{error || 'Something went wrong with your payment.'}</p>
            
            <div className="space-y-3">
              <Button
                data-testid="retry-btn"
                className="w-full h-12 bg-bronze hover:bg-bronze-600 font-oswald uppercase tracking-wider"
                onClick={() => navigate('/subscription')}
              >
                Try Again
              </Button>
              <Button
                data-testid="dashboard-btn"
                variant="outline"
                className="w-full h-12 border-white/20 font-oswald uppercase tracking-wider"
                onClick={() => navigate('/dashboard')}
              >
                Back to Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
